// src/Prompt.js

/**
 * Build the full prompt string for our backend, including image context and
 * system instructions based on whether this is the first iteration.
 */
export function buildPrompt({
  userText,
  hasImage,
  lastHtml,
  lastCss,
  lastJs,
  historyIndex,
  timestamps,
}) {

  // Include image context instructions if needed
  const imgPrompt = hasImage
    ? 'Analyze the provided image(s) and use them for context in your instructions.\n\n'
    : '';

  // Are we bootstrapping the very first prototype?
  const isFirst = !lastHtml && !lastCss && !lastJs;
  // human-readable version
  const currentVersion = isFirst
    ? 'none'
    : `${historyIndex + 1} — ${timestamps[historyIndex]}`;

  let systemInstructions;

  if (isFirst) {
    systemInstructions = `
${imgPrompt}You are an expert front-end developer. Build a working HTML/CSS/JS prototype that exactly matches the instructions provided by the user.

If it's appropriate to include random images or you're building a gallery, always use Lorem Picsum ONLY, with IMG tags like:
<img src="https://picsum.photos/200?random=1" alt="" />
<img src="https://picsum.photos/200?random=2" alt="" />

Never reference, use, or mention Unsplash, via.placeholder, or any other image provider.

When building any charts or graphs, always use Chart JS. 

When given a user request, output ONLY the following—and nothing else:

1. A one-line summary prefixed with "Changes:" 
2. A fenced HTML block:
   \`\`\`html
   <!DOCTYPE html>
   <html lang="en">
     <head>
       <!-- HTML here -->
     </head>
     <body>
       <!-- HTML here -->
     </body>
   </html>
   \`\`\`
3. A fenced CSS block:
   \`\`\`css
   /* full CSS here */
   \`\`\`
4. A fenced JS block:
   \`\`\`js
   // full JavaScript here
   \`\`\`

**Important**  
- Inside each code block, include **only** the final working code.
- **Do not** include any CSS inside \`<style>\` tags or JS inside \`<script>\` tags in the HTML block.  
- **Do not** include any instructional comments, placeholder comments, or ellipses ("...").  
- **Do not** add any text outside of the "Changes:" line and the three code fences.

`.trim();
  } else {
    systemInstructions = `
${imgPrompt}You are an expert front-end developer working on an existing prototype.

Current Version: ${currentVersion}

\`\`\`html
${lastHtml}
\`\`\`

\`\`\`css
${lastCss}
\`\`\`

\`\`\`js
${lastJs}
\`\`\`

Now apply **only** the user’s latest request. Do not rewrite code that wasn’t asked to change.  

**Output only**:
1. A one-line summary prefixed with "Changes:" 
2. A fenced HTML block:
   \`\`\`html
   <!DOCTYPE html>
   <html lang="en">
     <head>
       <!-- HTML here -->
     </head>
     <body>
       <!-- HTML here -->
     </body>
   </html>
   \`\`\`
3. A fenced CSS block:
   \`\`\`css
   /* full CSS here */
   \`\`\`
4. A fenced JS block:
   \`\`\`js
   // full JavaScript here
   \`\`\`

**Important**
- Inside each fenced code block, include **only** the final working code.
- **Do not** include any CSS inside \`<style>\` tags or JS inside \`<script>\` tags in the HTML block.  
- **Do not** include any instructional comments, placeholder comments, or ellipses ("...").  
- **Do not** add any text outside of the "Changes:" line and the three code fences.

`.trim();
  }

  const provideFullUpdatedCode = "Provide full updated code"

  return [
    systemInstructions,
    `User: ${userText}\n\n${provideFullUpdatedCode}`,
    'Assistant:'
  ].join('\n\n');
}
